import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Instagram, Linkedin, Clock } from "lucide-react";
import Reveal from "../components/Reveal";
import MagneticButton from "../components/MagneticButton";
import { CONTACT, COUNTRIES, waLink } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", country: "", message: "" });
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { kind: "contact", ...form });
      toast.success("Message sent. We respond within 24 hours.");
      setForm({ name: "", email: "", phone: "", country: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <>
      <section data-testid="contact-hero" className="pt-36 pb-12 border-b border-[#B5896E]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="caption text-[#B5896E] mb-5 flex items-center gap-3"><span className="w-8 h-[1px] bg-[#B5896E]" />Contact</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] max-w-4xl">Let's build something <em className="italic text-[#B5896E]">together.</em></h1>
          <p className="mt-6 text-base lg:text-lg text-[#4A3F35]/70 max-w-2xl">Whether you're a store owner, a procurement manager or a curious retailer — we respond within 24 hours.</p>
        </div>
      </section>

      <section data-testid="contact-main" className="grid grid-cols-1 lg:grid-cols-2 min-h-[75vh]">
        <Reveal as="div" className="p-8 md:p-12 lg:p-20 bg-[#FAF6F0] border-b lg:border-b-0 lg:border-r border-[#B5896E]/10">
          <div className="max-w-xl">
            <div className="caption text-[#B5896E] mb-4">Send a message</div>
            <h2 className="font-display text-3xl lg:text-4xl mb-8">Tell us what you need.</h2>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Full name *">
                  <input data-testid="contact-field-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={INPUT} />
                </Field>
                <Field label="Email *">
                  <input data-testid="contact-field-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={INPUT} />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Phone">
                  <input data-testid="contact-field-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={INPUT} />
                </Field>
                <Field label="Country">
                  <select data-testid="contact-field-country" value={form.country} onChange={(e) => set("country", e.target.value)} className={INPUT}>
                    <option value="">Select country</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Message *">
                <textarea data-testid="contact-field-message" required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className={INPUT + " resize-none"} placeholder="Tell us about your store, what you're sourcing, volumes…" />
              </Field>
              <div className="pt-2">
                <MagneticButton testId="contact-submit" type="submit" variant="primary">{loading ? "Sending…" : "Send message"}</MagneticButton>
              </div>
            </form>
          </div>
        </Reveal>

        <Reveal as="div" delay={150} className="p-8 md:p-12 lg:p-20 bg-[#E8DDD0]/35 relative overflow-hidden">
          <div className="max-w-xl">
            <div className="caption text-[#B5896E] mb-4">Direct lines</div>
            <h2 className="font-display text-3xl lg:text-4xl mb-10">We're a message away.</h2>

            <div className="space-y-7">
              {CONTACT.phones.map((p) => (
                <Info key={p.label} icon={MessageCircle} label={`WhatsApp · ${p.label}`} value={p.number} link={waLink(`Hello Blue Velvet, I'd like to reach ${p.label}.`, p)} testId={`contact-info-whatsapp-${p.label.toLowerCase()}`} />
              ))}
              <Info icon={Mail} label="Email" value={CONTACT.email} link={`mailto:${CONTACT.email}`} testId="contact-info-email" />
              <Info icon={MapPin} label="Branches" value={
                <div className="flex flex-wrap gap-2 mt-1">
                  {CONTACT.branches.map((b) => (
                    <span key={b} data-testid={`contact-branch-${b.toLowerCase()}`} className="px-3 py-1.5 text-xs tracking-[0.18em] uppercase text-[#4A3F35]/85 border border-[#B5896E]/35 rounded-full bg-white/70">
                      {b}
                    </span>
                  ))}
                </div>
              } testId="contact-info-branches" />
              <Info icon={MapPin} label="Registered office" value={<><span className="block">{CONTACT.addressLine1}</span><span className="text-[#4A3F35]/60 text-sm">{CONTACT.addressLine2}</span></>} testId="contact-info-address" />
              <Info icon={Clock} label="Response time" value="Within 24 hours, every business day." testId="contact-info-hours" />
            </div>

            <div className="mt-10 pt-8 border-t border-[#B5896E]/15">
              <div className="caption text-[#4A3F35]/60 mb-4">Follow</div>
              <div className="flex items-center gap-4">
                <a data-testid="contact-instagram" href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" className="w-11 h-11 grid place-items-center border border-[#B5896E]/30 rounded-full hover:border-[#B5896E] hover:text-[#B5896E] transition-colors"><Instagram size={16} /></a>
                <a data-testid="contact-linkedin" href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" className="w-11 h-11 grid place-items-center border border-[#B5896E]/30 rounded-full hover:border-[#B5896E] hover:text-[#B5896E] transition-colors"><Linkedin size={16} /></a>
              </div>
            </div>

            <div className="mt-10">
              <MagneticButton testId="contact-cta-whatsapp" variant="primary" href={waLink()}>Open WhatsApp chat</MagneticButton>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}

const INPUT = "w-full bg-[#FAF6F0] border border-[#B5896E]/20 text-[#4A3F35] px-4 py-3 rounded-sm focus:outline-none focus:border-[#B5896E] text-sm transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="caption text-[#4A3F35]/60 mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function Info({ icon: Icon, label, value, link, testId }) {
  const inner = (
    <div data-testid={testId} className="flex items-start gap-5 group">
      <div className="w-11 h-11 grid place-items-center border border-[#B5896E]/30 rounded-full shrink-0 group-hover:border-[#B5896E] transition-colors">
        <Icon size={16} className="text-[#B5896E]" />
      </div>
      <div>
        <div className="caption mb-1">{label}</div>
        <div className="text-base text-[#4A3F35] group-hover:text-[#B5896E] transition-colors">{value}</div>
      </div>
    </div>
  );
  if (link) return <a href={link} target={link.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">{inner}</a>;
  return inner;
}
